/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import DataActionRecipientCondition from 'cio/app-models/data-action/recipient-condition';
import DS from 'ember-data';
import { action, computed } from '@ember/object';
import EnvironmentModel from 'cio/models/environment';
import {
  DataActionComposerSectionType,
  DataActionConditionType,
  DataActionTriggerAttributeDropdownItem,
} from 'cio/utils/campaigns/data-action';
import ComposerEditableEntity from 'cio/utils/composer/composer-editable-entity';
import TemplateModel from 'cio/models/template';
import { sortByCaseInsensitive } from 'cio/utils/sort';

interface CreateEventRecipientConditionEditorArgs {
  editableEntity: ComposerEditableEntity;
  recipientCondition: DataActionRecipientCondition;
  autocompleteValues: Partial<Record<DataActionConditionType, { label: string; value: string; path: string[] }[]>>;
  supportedConditionTypes: DataActionConditionType[];
  isLoadingAsyncAutocompleteValues: boolean;
  updateHighlight?: (highlight: DataActionTriggerAttributeDropdownItem | null) => unknown;
  liquidFieldFocusIn?: (fieldName: string, index?: number) => unknown;
  liquidFieldFocusOut?: () => unknown;
  hasReadOnlyPermission: boolean;
}

export default class CreateEventRecipientConditionEditor extends Component<CreateEventRecipientConditionEditorArgs> {
  @service store!: DS.Store;

  DataActionComposerSectionType = DataActionComposerSectionType;
  DataActionConditionType = DataActionConditionType;

  get environmentOptions() {
    return sortByCaseInsensitive(
      this.store.peekAll('environment').filter((environment) => !environment.isNew),
      ['name'],
    ).map((environment) => ({
      model: environment,
      label: environment.name,
      value: Number(environment.id),
    }));
  }

  @computed('args.editableEntity.template.recipientEnvironmentId')
  get recipientEnvironmentId() {
    return this.args.editableEntity.template?.recipientEnvironmentId;
  }

  get selectedEnvironmentOption() {
    if (this.recipientEnvironmentId == null) {
      return null;
    }

    let option = this.environmentOptions.find((e) => e.value === Number(this.recipientEnvironmentId));

    // If the selected environment can't be found in the environment list, it
    // means the current user doesn't have access to that environment.
    // In this case, we'll show a "Private workspace" option.
    if (option == null) {
      return {
        label: 'Private workspace',
        value: null,
        classes: 'text-gray-500',
      };
    }

    return option;
  }

  get selectedEnvironment() {
    if (this.recipientEnvironmentId == null) {
      return null;
    }
    return this.store.peekRecord('environment', this.recipientEnvironmentId);
  }

  @action
  changeIdentifierIfNecessary() {
    let currentIdentifier = this.args.recipientCondition.field;
    let availableIdentifiers = (this.selectedEnvironment?.allEnabledIdentifiers ?? []) as string[];

    if (availableIdentifiers.includes(currentIdentifier)) {
      return;
    }

    this.selectIdentifier(availableIdentifiers[0]);
  }

  @action
  selectEnvironmentId(item: { model: EnvironmentModel; value: number; label: string }) {
    (this.args.editableEntity.template as TemplateModel).recipientEnvironmentId = item.value;

    this.changeIdentifierIfNecessary();
  }

  @action
  selectIdentifier(identifier: string) {
    this.args.recipientCondition.field = identifier;
  }

  @action
  updateHighlight(highlight: DataActionTriggerAttributeDropdownItem | null) {
    this.args.updateHighlight?.(highlight);
  }

  @action
  liquidFieldFocusIn(fieldName: string, index?: number) {
    this.args.liquidFieldFocusIn?.(fieldName, index);
  }

  @action
  liquidFieldFocusOut() {
    this.args.liquidFieldFocusOut?.();
  }
}
